import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch, HiChevronDown, HiX } from 'react-icons/hi';
import { Customer } from '@/types/customer';
import { customerApi } from '@/api/endpoints/customers';

interface CustomerSearchInputProps {
  selectedCustomer?: Customer | null;
  onCustomerChange: (customer: Customer | null) => void;
  disabled?: boolean;
  error?: string;
}

const CustomerSearchInput: React.FC<CustomerSearchInputProps> = ({
  selectedCustomer,
  onCustomerChange,
  disabled = false,
  error
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [shouldLoadOnFocus, setShouldLoadOnFocus] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search customers with debounce
  useEffect(() => {
    const searchCustomers = async () => {
      setLoading(true);
      try {
        const response = await customerApi.getCustomers(1, 20, {
          search: searchQuery || undefined // Allow empty search to get all customers
        });
        setCustomers(response.data);
      } catch (error) {
        console.error('Error searching customers:', error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!isOpen && value.length > 0) {
      setIsOpen(true);
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    onCustomerChange(customer);
    setSearchQuery('');
    setIsOpen(false);
    setCustomers([]);
  };

  const handleClearSelection = () => {
    onCustomerChange(null);
    setSearchQuery('');
    setCustomers([]);
    setShouldLoadOnFocus(true); // Reset to allow loading on next focus
    inputRef.current?.focus();
  };

  const handleSelectedCustomerClick = () => {
    if (!disabled) {
      handleClearSelection();
    }
  };

  const handleInputFocus = async () => {
    setIsOpen(true);
    // If no search query and should load on focus, trigger API call with empty search
    if (searchQuery.length === 0 && shouldLoadOnFocus && customers.length === 0) {
      setLoading(true);
      try {
        const response = await customerApi.getCustomers(1, 20, {
          search: undefined // Empty search to get initial customers
        });
        setCustomers(response.data);
        setShouldLoadOnFocus(false); // Prevent repeated calls
      } catch (error) {
        console.error('Error loading customers on focus:', error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        {selectedCustomer ? (
          // Display selected customer
          <div
            className={`
              flex items-center justify-between w-full px-3 py-2 border rounded-md shadow-sm bg-white
              ${error ? 'border-red-300' : 'border-gray-300'}
              ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            onClick={handleSelectedCustomerClick}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {selectedCustomer.name}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {selectedCustomer.code} • {selectedCustomer.phone}
              </div>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={handleClearSelection}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <HiX className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          // Search input
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={t('receiptVoucher.form.customerSearchPlaceholder')}
              className={`
                block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm
                ${error ? 'border-red-300' : 'border-gray-300'}
                ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}
              `}
              disabled={disabled}
            />
            <HiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !selectedCustomer && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <div className="mt-2 text-sm">{t('common.loading')}</div>
            </div>
          ) : customers.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
              {customers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => handleCustomerSelect(customer)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {customer.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {customer.code} • {customer.phone}
                      </div>
                    </div>
                    <div className="ml-2 text-right">
                      <div className="text-xs text-gray-500">
                        {t('receiptVoucher.form.debt')}: {customer.totalDebt?.toLocaleString() || 0} VND
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <div className="text-sm">
                {searchQuery.length > 0
                  ? t('receiptVoucher.form.noCustomersFound')
                  : t('receiptVoucher.form.noCustomersAvailable')
                }
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CustomerSearchInput;
